import {
  DocumentRef, VideoRef, Document, Article, Video, Scenes,
} from '../../../types/items';
import { ItemList } from '../../../types/main';
import { updateItemByState } from '../../util';
import { DocumentDownloadUrl, VideoDownloadUrl, VideoRefSyncResponse } from './types';
import { DocumentPage, DocumentPagesPayload } from './types/documentPages';
import { parseJsonToEditorStateWithInstruction } from '../../../services/editorJS/utils';
import { DocumentPageInstruction, ImageInstruction, VideoInstruction } from '../../../types/instructions';

export const transformDocumentToRef = (document: Document) => ({
  id: document.id,
  title: document.title,
  media_type_id: document.media_type_id,
  mime_type_id: document.mime_type_id,
  pages_render_state_id: document.pages_render_state_id,
  thumbnails: document.ref.thumbnails,
  ref: {
    is_processing: document.ref.is_processing,
    is_uploading: document.ref.is_uploading,
    sse_topic: document.ref.sse_topic,
    media_type: document.ref.media_type,
    pages_render_state: document.ref.pages_render_state,
    filename_base: document.ref.filename_base,
  },
} as DocumentRef);

export const transformVideoRef = (video: Video) => ({
  id: video.id,
  title: video.title,
  streaming_url: video.streaming_url,
  generated_thumbnails: video.generated_thumbnails,
  upload_status_id: video.upload_status_id,
  playback_token: video.ref.playback_token,
  duration: video.duration,
  ref: {
    is_processing: video.ref.is_processing,
    is_uploading: video.ref.is_uploading,
    thumbnails: video.ref.thumbnails,
    sse_topic: video.ref.sse_topic,
    upload_status: video.ref.upload_status,
    transcoding_status: video.ref.transcoding_status,
    statics: video.ref.statics,
    filename_base: video.ref.filename_base,
  },
} as VideoRef);

export const transformDocumentPage = (payload: DocumentPagesPayload): DocumentPage[] => {
  const fullPages = payload.full_pages[0];
  const thumbnails = payload.thumbnails[0];
  const pages: DocumentPage[] = [];
  const truncateFilename = (url: string) => {
    const split = url.split('/');
    return split[split.length - 1].split('?')[0];
  };

  fullPages.forEach((item, key) => {
    pages.push({
      id: item.page,
      url: item.url,
      filename: truncateFilename(item.url.url),
      thumbnails: thumbnails[key].thumbnails.map((thumb) => ({
        width: thumb.width,
        height: thumb.height,
        signed_url: thumb.url,
        filename: truncateFilename(item.url.url),
      })),
    });
  });

  return pages;
};

export const updateStateByDocument = (state: ItemList<Article> | null, document: Document) => {
  if (state !== null) {
    const article = state.items.find((item) => item.document_id === document.id);
    if (article) {
      article.ref.document = transformDocumentToRef(document);
      return updateItemByState(state, article);
    }
  }
  return state;
};

export const updateStateByVideo = (state: ItemList<Article> | null, video: Video) => {
  if (state !== null) {
    const article = state.items.find((item) => item.video_id === video.id);
    if (article) {
      article.ref.video = transformVideoRef(video);

      return updateItemByState(state, article);
    }
  }
  return state;
};

export const updateArticleVideoRef = (state: ItemList<Article> | null, syncVideoRef: VideoRefSyncResponse) => {
  if (state !== null) {
    const article = state.items.find((item) => item.video_id === syncVideoRef.video_master_id);
    if (article && article?.ref.video?.ref) {
      article.ref.video.ref = {
        ...article.ref.video.ref,
        is_processing: syncVideoRef.refs.is_processing,
        is_uploading: syncVideoRef.refs.is_uploading,
      };
      return updateItemByState(state, article);
    }
  }
  return state;
};

export const updateArticleDocumentDownloadUrl = (state: ItemList<Article> | null, documentDownloadUrl: DocumentDownloadUrl) => {
  if (state !== null) {
    const article = state.items.find((item) => item.document_id === documentDownloadUrl.documentId);
    if (article) {
      article.document_download_url = documentDownloadUrl.signedUrl;
      return updateItemByState(state, article);
    }
  }
  return state;
};

export const updateArticleVideoDownloadUrl = (state: ItemList<Article> | null, videoDownloadUrl: VideoDownloadUrl) => {
  if (state !== null) {
    const article = state.items.find((item) => item.video_id === videoDownloadUrl.videoId);
    if (article) {
      article.video_download_url = videoDownloadUrl.signedUrl;
      return updateItemByState(state, article);
    }
  }
  return state;
};
export interface ArticlesWithNumbers {
  article: Article;
  pageNumber: number;
}
export type OutputDataFromFindArticlesInScene = (Article | ArticlesWithNumbers);

export const findArticlesInScene = (scene: Scenes, articles:Article[]): OutputDataFromFindArticlesInScene[] => {
  const { instructions } = parseJsonToEditorStateWithInstruction(scene.script);

  const foundArticles:OutputDataFromFindArticlesInScene[] = [];
  instructions.forEach((instruction) => {
    // eslint-disable-next-line no-nested-ternary
    const articleId = (instruction as VideoInstruction).data.videoId
    && (instruction as VideoInstruction).data.videoId !== -1
      ? (instruction as VideoInstruction).data.videoId : (instruction as ImageInstruction).data.documentId
      && (instruction as ImageInstruction).data.documentId !== -1
        ? (instruction as ImageInstruction).data.documentId : null;
    if (articleId) {
      const foundArticle = articles.find((item) => (item.video_id === articleId || item.document_id === articleId));
      if (foundArticle) {
      // if(!foundArticles.some((item) => ((item.video_id === foundArticle.video_id && item.video_id !== -1) || (item.document_id === foundArticle.document_id && item.document_id !== - 1))))
        if (foundArticle.ref.document?.media_type_id === 4) {
          foundArticles.push({ article: foundArticle, pageNumber: (instruction as DocumentPageInstruction).data.page });
        } else {
          foundArticles.push(foundArticle);
        }
      }
    }
  });
  return foundArticles;
};

export const findAndDeleteSceneFromSceneScript = (scenes: Scenes[], articles: Article[], articleId: number) => {
  const newScenes = [...scenes];
  let foundScenes: Scenes[] = [];
  newScenes.forEach((scene) => {
    const { instructions, ...other } = parseJsonToEditorStateWithInstruction(scene.script);
    const FoundArticle = articles.find((article) => article.id === articleId);
    if (FoundArticle) {
      const foundMediaId = FoundArticle.video_id > -1 ? FoundArticle.video_id : FoundArticle.document_id;
      if (instructions) {
        instructions.forEach((instruction) => {
          if ((instruction as ImageInstruction).data.documentId === foundMediaId) {
            (instruction as ImageInstruction).data.documentId = -2;
            scene.script = JSON.stringify({ instructions, ...other });
            foundScenes.push(scene);
          }
          if ((instruction as VideoInstruction).data.videoId === foundMediaId) {
            (instruction as VideoInstruction).data.videoId = -2;
            scene.script = JSON.stringify({ instructions, ...other });
            foundScenes.push(scene);
          }
        });
      }
    }
  });
  return foundScenes;
};

export const numberingForArticleDownload = (number: number): string => {
  const stringNumber = number.toString();

  switch (stringNumber.length) {
    case 1:
      return `000${stringNumber}`;
    case 2:
      return `00${stringNumber}`;
    case 3:
      return `0${stringNumber}`;
    default:
    case 4:
      return stringNumber;
  }
};
